import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Popper, Fade, Paper, Typography, Box, alpha, useMediaQuery } from '@mui/material';
import type { PopperPlacementType } from '@mui/material/Popper';
import { useTheme } from '@mui/material/styles';
import { ConfirmDialog } from './dialog';
import { dismissProductOnboarding } from '../hooks/useProductOnboarding';
import type { ProductStepId } from '../hooks/useProductOnboarding';
import { trackProductOnboardingSkipped } from '../utils/analytics';

/** Arrow points from the card toward the anchor: bottom placement → top edge; top placement → bottom edge. */
function arrowDirectionForPlacement(placement: PopperPlacementType | undefined): 'up' | 'down' {
  if (!placement) return 'down';
  return placement.startsWith('bottom') ? 'up' : 'down';
}

/**
 * Keep flip on the same axis as the primary placement. Otherwise `bottom-end` often flips to `top-*`
 * first; on mobile that parks the coachmark under the sticky Getting started banner instead of the button.
 */
function flipFallbackPlacements(primary: PopperPlacementType): PopperPlacementType[] {
  if (primary.startsWith('bottom')) return ['bottom-start', 'bottom', 'bottom-end'];
  if (primary.startsWith('top')) return ['top-start', 'top', 'top-end'];
  if (primary.startsWith('left')) return ['left-start', 'left', 'left-end'];
  if (primary.startsWith('right')) return ['right-start', 'right', 'right-end'];
  return ['top', 'top-end', 'top-start', 'bottom-end', 'bottom-start'];
}

function readCoachmarkAnchor(
  anchorElProp: HTMLElement | null | undefined,
  anchorRef: React.RefObject<Element | null> | undefined,
  getAnchorElement: (() => HTMLElement | null) | undefined,
): HTMLElement | null {
  const el = (getAnchorElement?.() ?? anchorRef?.current ?? anchorElProp) ?? null;
  return el instanceof HTMLElement ? el : null;
}

/**
 * Stable Popper reference with a fresh `getBoundingClientRect` each time Popper measures.
 * Passing a raw HTMLElement can leave MUI Popper stuck with a zero or pre-layout rect after reload
 * or inside scrollable main content; the virtual ref always tracks the live node.
 */
function createCoachmarkVirtualAnchor(
  sourcesRef: React.MutableRefObject<{
    anchorElProp: HTMLElement | null | undefined;
    anchorRef: React.RefObject<Element | null> | undefined;
    getAnchorElement: (() => HTMLElement | null) | undefined;
  }>,
) {
  return {
    getBoundingClientRect(): DOMRect {
      const { anchorElProp, anchorRef, getAnchorElement } = sourcesRef.current;
      const el = readCoachmarkAnchor(anchorElProp, anchorRef, getAnchorElement);
      return el ? el.getBoundingClientRect() : new DOMRect(0, 0, 0, 0);
    },
    get contextElement(): Element | undefined {
      const { anchorElProp, anchorRef, getAnchorElement } = sourcesRef.current;
      return readCoachmarkAnchor(anchorElProp, anchorRef, getAnchorElement) ?? undefined;
    },
  };
}

/**
 * `anchorEl={ref.current}` breaks on first paint: refs attach after render, and ref updates don’t
 * re-render the parent, so Popper can stay stuck with null or a stale rect until something else
 * updates. Reading `anchorRef.current` / `getAnchorElement()` in layout effect + rAF fixes reloads.
 */
function useResolvedCoachmarkAnchor(
  open: boolean,
  anchorElProp: HTMLElement | null | undefined,
  anchorRef: React.RefObject<Element | null> | undefined,
  getAnchorElement: (() => HTMLElement | null) | undefined,
): HTMLElement | null {
  const [resolved, setResolved] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setResolved(null);
      return;
    }

    const read = (): HTMLElement | null =>
      readCoachmarkAnchor(anchorElProp, anchorRef, getAnchorElement);

    let cancelled = false;
    let rafId = 0;
    let attempts = 0;
    const maxAttempts = 48;

    const tick = () => {
      if (cancelled) return;
      const el = read();
      if (el) {
        setResolved((prev: HTMLElement | null) => (prev === el ? prev : el));
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        rafId = requestAnimationFrame(tick);
      } else {
        setResolved(null);
      }
    };

    const first = read();
    if (first) {
      setResolved(first);
    } else {
      setResolved(null);
      rafId = requestAnimationFrame(tick);
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [open, anchorElProp, anchorRef, getAnchorElement]);

  return resolved;
}

interface OnboardingCoachmarkProps {
  open: boolean;
  /**
   * Live element, e.g. from state. Prefer `anchorRef` or `getAnchorElement` when anchoring to a
   * ref-backed control so the first frame after load resolves correctly.
   */
  anchorEl?: HTMLElement | null;
  /** Ref to the control; read after mount so Popper isn’t left with null on reload. */
  /** Use the same ref you pass to the target control (`useRef<HTMLButtonElement>(null)` is fine). */
  anchorRef?: React.RefObject<Element | null>;
  /** When the target switches between nodes (collapsed vs expanded), return the active element. */
  getAnchorElement?: () => HTMLElement | null;
  title?: string;
  message: string;
  placement?: PopperPlacementType;
  zIndex?: number;
  maxWidth?: number;
  offsetX?: number;
  /** @deprecated Ignored. Arrow follows Popper’s resolved placement (including after flip). */
  arrowDirection?: 'up' | 'down';
  /**
   * `auto` — minimal on small screens, card on larger screens.
   * `card` — full tooltip (title, body, accent ring).
   * `minimal` — small one-line hint + tiny arrow (for mobile / tight UI).
   */
  variant?: 'auto' | 'card' | 'minimal';
  /**
   * Show a subtle "Skip setup" action that opens a confirm modal and dismisses
   * the whole product-onboarding guide. Defaults to true (these are onboarding nudges).
   */
  enableSkip?: boolean;
  /** Current onboarding step id, forwarded to analytics when the guide is skipped. */
  skipStepId?: ProductStepId | null;
}

export default function OnboardingCoachmark({
  open,
  anchorEl: anchorElProp,
  anchorRef,
  getAnchorElement,
  title = 'Next step',
  message,
  placement = 'top',
  zIndex = 1700,
  maxWidth = 260,
  offsetX = 0,
  variant = 'auto',
  enableSkip = true,
  skipStepId = null,
}: OnboardingCoachmarkProps) {
  const resolvedAnchor = useResolvedCoachmarkAnchor(open, anchorElProp, anchorRef, getAnchorElement);
  const [confirmSkip, setConfirmSkip] = useState(false);

  const handleSkipConfirm = async () => {
    setConfirmSkip(false);
    trackProductOnboardingSkipped({ source: 'coachmark', current_step_id: skipStepId });
    await dismissProductOnboarding();
  };

  const anchorSourcesRef = React.useRef({
    anchorElProp: undefined as HTMLElement | null | undefined,
    anchorRef: undefined as React.RefObject<Element | null> | undefined,
    getAnchorElement: undefined as (() => HTMLElement | null) | undefined,
  });
  anchorSourcesRef.current = { anchorElProp, anchorRef, getAnchorElement };

  const virtualAnchorRef = React.useRef<ReturnType<typeof createCoachmarkVirtualAnchor> | null>(null);
  if (virtualAnchorRef.current == null) {
    virtualAnchorRef.current = createCoachmarkVirtualAnchor(anchorSourcesRef);
  }

  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const arrowBg = isDark ? alpha(theme.palette.background.paper, 0.96) : '#fff';
  const pm = theme.palette.primary.main;
  /** Tighter halo on phones so small targets (e.g. Pin) aren’t overwhelmed */
  const spotlightRing = isNarrow
    ? `0 0 0 2px ${alpha(pm, 0.22)}`
    : `0 0 0 3px ${alpha(pm, 0.2)}`;
  const spotlightPulse = isNarrow
    ? `0 0 0 4px ${alpha(pm, 0.06)}`
    : `0 0 0 8px ${alpha(pm, 0.08)}`;
  const anchorBorderAlpha = isNarrow ? 0.42 : 0.55;
  const effectiveVariant: 'card' | 'minimal' =
    variant === 'auto' ? (isNarrow ? 'minimal' : 'card') : variant;

  const popperModifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [offsetX, 0] } },
      {
        name: 'preventOverflow',
        options: { padding: 8, altAxis: true, tether: !isNarrow },
      },
      { name: 'flip', options: { fallbackPlacements: flipFallbackPlacements(placement) } },
    ],
    [offsetX, placement, isNarrow],
  );

  const popperOptions = useMemo(
    () => (isNarrow ? { strategy: 'fixed' as const } : undefined),
    [isNarrow],
  );

  useEffect(() => {
    if (!resolvedAnchor || !open) return;
    const previousTransition = resolvedAnchor.style.transition;
    const previousBoxShadow = resolvedAnchor.style.boxShadow;
    const previousBorderColor = resolvedAnchor.style.borderColor;
    resolvedAnchor.style.transition = 'box-shadow 180ms ease, border-color 180ms ease';
    resolvedAnchor.style.boxShadow = `${spotlightRing}, ${spotlightPulse}`;
    resolvedAnchor.style.borderColor = alpha(theme.palette.primary.main, anchorBorderAlpha);

    return () => {
      resolvedAnchor.style.transition = previousTransition;
      resolvedAnchor.style.boxShadow = previousBoxShadow;
      resolvedAnchor.style.borderColor = previousBorderColor;
    };
  }, [resolvedAnchor, open, anchorBorderAlpha, spotlightPulse, spotlightRing, theme.palette.primary.main]);

  const minimalArrow = (arrowDirection: 'up' | 'down') => (
    <Box
      sx={{
        position: 'absolute',
        right: 10,
        width: 8,
        height: 8,
        bgcolor: arrowBg,
        borderRight: (t) => `1.5px solid ${alpha(t.palette.primary.main, 0.3)}`,
        borderBottom: (t) => `1.5px solid ${alpha(t.palette.primary.main, 0.3)}`,
        transform: arrowDirection === 'down' ? 'rotate(45deg)' : 'rotate(225deg)',
        ...(arrowDirection === 'down' ? { bottom: -4, zIndex: 2 } : { top: -4, zIndex: 2 }),
      }}
    />
  );

  const popperOpen = open && resolvedAnchor != null;

  const skipPill = enableSkip ? (
    <Box
      component="button"
      type="button"
      onClick={(e) => { e.stopPropagation(); setConfirmSkip(true); }}
      sx={(t) => ({
        appearance: 'none', cursor: 'pointer', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center',
        px: 0.75, py: 0.2, m: 0,
        borderRadius: `${t.ds.radius.full}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
        color: t.tokens.text.tertiary,
        fontSize: '0.625rem', fontWeight: 600, lineHeight: 1.5,
        letterSpacing: '0.02em', whiteSpace: 'nowrap',
        transition: 'color 0.15s, border-color 0.15s, background 0.15s',
        '&:hover': { color: t.tokens.text.secondary, borderColor: t.tokens.border.default },
      })}
    >
      Skip setup
    </Box>
  ) : null;

  return (
    <>
    <Popper
      open={popperOpen}
      anchorEl={popperOpen ? virtualAnchorRef.current : null}
      placement={placement}
      transition
      sx={{ zIndex }}
      modifiers={popperModifiers}
      popperOptions={popperOptions}
    >
      {({ TransitionProps, placement: resolvedPlacement }: { TransitionProps?: object; placement?: PopperPlacementType }) => {
        const arrowDirection = arrowDirectionForPlacement(resolvedPlacement);

        if (effectiveVariant === 'minimal') {
          return (
            <Fade {...TransitionProps} timeout={180}>
              <Box
                sx={{
                  position: 'relative',
                  my: 0.25,
                  maxWidth: `min(${Math.min(maxWidth, 220)}px, calc(100vw - 20px))`,
                  /* Static purple ring — same accent language as desktop card (spinning border is sm+ only) */
                  borderRadius: '10px',
                  p: '1.5px',
                  background: `linear-gradient(135deg,
                    ${alpha(pm, isDark ? 0.38 : 0.42)} 0%,
                    ${alpha(pm, 0.2)} 45%,
                    ${alpha(pm, isDark ? 0.32 : 0.36)} 100%)`,
                  boxShadow: `0 2px 12px ${alpha(pm, 0.14)}, 0 1px 3px ${alpha(theme.palette.common.black, isDark ? 0.22 : 0.06)}`,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    position: 'relative',
                    py: 0.55,
                    pl: 0.85,
                    pr: 1.75,
                    pb: 0.65,
                    borderRadius: '8.5px',
                    bgcolor: arrowBg,
                    border: 'none',
                    boxShadow: 'none',
                  }}
                >
                  <Typography
                    sx={(t) => ({
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                      fontWeight: 600,
                      color: t.tokens.text.primary,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      pr: 0.25,
                    })}
                  >
                    {message}
                  </Typography>
                  {skipPill && (
                    <Box sx={{ mt: 0.4, display: 'flex', justifyContent: 'flex-end' }}>
                      {skipPill}
                    </Box>
                  )}
                  {minimalArrow(arrowDirection)}
                </Paper>
              </Box>
            </Fade>
          );
        }

        return (
        <Fade {...TransitionProps} timeout={220}>
          {/* Outer box: no overflow hidden so the arrow tip isn't clipped */}
          <Box sx={{
            position: 'relative',
            my: { xs: 0.5, sm: 1 },
            maxWidth: `min(${maxWidth}px, calc(100vw - 24px))`,
          }}
          >

            {/* Spinning border — slimmer ring on narrow viewports */}
            <Box sx={{
              position: 'absolute',
              inset: { xs: '-1px', sm: '-1.5px' },
              borderRadius: { xs: '10px', sm: '13px' },
              overflow: 'hidden',
              zIndex: 0,
              pointerEvents: 'none',
              display: { xs: 'none', sm: 'block' },
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '200%',
                height: '200%',
                top: '-50%',
                left: '-50%',
                background: `conic-gradient(from 0deg,
                  ${alpha(theme.palette.primary.main, 0.18)} 0%,
                  ${alpha(theme.palette.primary.main, 0.18)} 38%,
                  ${alpha(theme.palette.primary.main, 0.4)} 50%,
                  ${theme.palette.primary.main} 57%,
                  rgba(255,255,255,0.9) 60%,
                  ${theme.palette.primary.main} 63%,
                  ${alpha(theme.palette.primary.main, 0.4)} 70%,
                  ${alpha(theme.palette.primary.main, 0.18)} 82%,
                  ${alpha(theme.palette.primary.main, 0.18)} 100%
                )`,
                animation: 'borderSpin 2.4s linear infinite',
                '@keyframes borderSpin': {
                  to: { transform: 'rotate(360deg)' },
                },
              },
            }} />

            {/* Card - sits on top, solid background reveals only the 1.5px border */}
            <Paper
              elevation={isNarrow ? 4 : 6}
              sx={{
                p: { xs: 1, sm: 1.25 },
                borderRadius: { xs: '10px', sm: '11.5px' },
                bgcolor: arrowBg,
                border: 'none',
                position: 'relative',
                zIndex: 1,
                boxShadow: isNarrow
                  ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}, 0 1px 4px ${alpha(theme.palette.common.black, isDark ? 0.25 : 0.06)}`
                  : `0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}, 0 2px 8px ${alpha(theme.palette.common.black, isDark ? 0.3 : 0.08)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: { xs: 0.4, sm: 0.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.6 }, minWidth: 0 }}>
                {/* Pulsing dot — semantic attention colour */}
                <Box component="span" sx={{ position: 'relative', width: { xs: 7, sm: 8 }, height: { xs: 7, sm: 8 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box component="span" sx={(t) => ({
                    position: 'absolute',
                    width: { xs: 7, sm: 8 },
                    height: { xs: 7, sm: 8 },
                    borderRadius: '50%',
                    bgcolor: t.tokens.semantic.warning,
                    opacity: 0.4,
                    animation: 'coachmarkPulse 1.6s ease-out infinite',
                    '@keyframes coachmarkPulse': {
                      '0%':   { transform: 'scale(1)', opacity: 0.5 },
                      '70%':  { transform: 'scale(2.1)', opacity: 0 },
                      '100%': { transform: 'scale(2.1)', opacity: 0 },
                    },
                  })} />
                  <Box component="span" sx={(t) => ({
                    position: 'relative',
                    width: { xs: 5, sm: 6 },
                    height: { xs: 5, sm: 6 },
                    borderRadius: '50%',
                    bgcolor: t.tokens.semantic.warning,
                  })} />
                </Box>
                <Typography sx={(t) => ({
                  ...t.typography.eyebrow,
                  fontSize: '0.6875rem',
                  color: t.tokens.semantic.warning,
                })}>
                  {title}
                </Typography>
                </Box>
                {skipPill}
              </Box>
              <Typography sx={(t) => ({
                fontSize: { xs: '0.8125rem', sm: '0.8125rem' },
                lineHeight: 1.45,
                color: t.tokens.text.primary,
              })}>
                {message}
              </Typography>

              {/* Arrow tip - inside Paper so it gets the card background */}
              <Box sx={{
                position: 'absolute',
                right: { xs: 14, sm: 22 },
                width: { xs: 10, sm: 12 },
                height: { xs: 10, sm: 12 },
                bgcolor: arrowBg,
                borderRight: (t) => `1.5px solid ${alpha(t.palette.primary.main, 0.3)}`,
                borderBottom: (t) => `1.5px solid ${alpha(t.palette.primary.main, 0.3)}`,
                transform: arrowDirection === 'down' ? 'rotate(45deg)' : 'rotate(225deg)',
                ...(arrowDirection === 'down' ? { bottom: { xs: -5, sm: -6 }, zIndex: 2 } : { top: { xs: -5, sm: -6 }, zIndex: 2 }),
              }} />
            </Paper>
          </Box>
        </Fade>
        );
      }}
    </Popper>
    {enableSkip && (
      <ConfirmDialog
        open={confirmSkip}
        onClose={() => setConfirmSkip(false)}
        onConfirm={handleSkipConfirm}
        title="Skip setup tips?"
        description="We'll stop showing these guided tips. You can still use every feature on your own."
        confirmLabel="Skip tips"
        tone="neutral"
      />
    )}
    </>
  );
}
